// src/components/layout/Sidebar.jsx

import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, Link } from 'react-router-dom';
import {
  FaThLarge, FaFileAlt, FaUsers, FaStar, FaCommentDots,
  FaInbox, FaEye, FaWallet, FaUser, FaCog, FaSignOutAlt,
  FaHeart, FaSearch, FaTimes, FaExclamationTriangle,
  FaCheckCircle, FaTimesCircle, FaTrophy,
  FaMoneyBillWave, FaBars,
} from 'react-icons/fa';
import logoImg from "../../Logo/logo.jpg";

const Sidebar = ({ isOpen, setIsOpen, isMobile }) => {
  const navigate = useNavigate();
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  // ✅ Screen size track karo
  useEffect(() => {
    const handleResize = () => setScreenWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ✅ Screen size flags
  const isTablet = screenWidth <= 1024;
  const isSmallMobile = screenWidth <= 480;
  const isTinyMobile = screenWidth <= 360;

  const navItems = [
    { path: '/dashboard', icon: FaThLarge, label: 'Dashboard' },
    { path: '/biodata', icon: FaFileAlt, label: 'Biodata Match' },
    { path: '/all-users', icon: FaUsers, label: 'All Users' },
    { path: '/shortlisted', icon: FaStar, label: 'Shortlisted' },
    { path: '/messages', icon: FaCommentDots, label: 'Messages' },
    { path: '/requests', icon: FaInbox, label: 'Requests' },
    { path: '/success-stories', icon: FaTrophy, label: 'Success Stories' },
    { path: '/visitors', icon: FaEye, label: 'Visitors' },
    { path: '/recharge', icon: FaWallet, label: 'Recharge' },
    // { path: '/bank-details', icon: FaUniversity, label: 'Bank Details' },
    { path: '/wallet', icon: FaMoneyBillWave, label: 'Wallet' },
    { path: '/profile', icon: FaUser, label: 'Profile' },
    { path: '/settings', icon: FaCog, label: 'Settings' },
  ];

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleLogoutConfirm = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token');
    localStorage.removeItem('user_id');
    localStorage.removeItem('username');
    localStorage.removeItem('role');
    localStorage.removeItem('refer');
    localStorage.removeItem('user');
    localStorage.removeItem('user_data');
    setShowLogoutModal(false);
    navigate('/User_Login');
  };

  const handleLogoutCancel = () => {
    setShowLogoutModal(false);
  };

  const closeSidebar = () => {
    if (isMobile) setIsOpen(false);
  };

  const getUsername = () => {
    return localStorage.getItem('username') || 'User';
  };

  // ✅ Dynamic sidebar width based on screen
  const getSidebarWidth = () => {
    if (isMobile) {
      if (isOpen) {
        return isTinyMobile ? '85vw' : isSmallMobile ? '80vw' : '75vw';
      }
      return '0px';
    }
    return isOpen ? '260px' : '80px';
  };

  return (
    <>
      <style>{`
        /* ===== KEYFRAMES ===== */
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(30px) scale(0.95); opacity: 0; }
          to { transform: translateY(0) scale(1); opacity: 1; }
        }
        @keyframes slideInLeft {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }

        /* ===== SIDEBAR BASE ===== */
        .rs-sidebar {
          position: fixed;
          left: 0;
          top: 0;
          height: 100vh;
          background: #ffffff;
          box-shadow: 2px 0 20px rgba(0, 0, 0, 0.08);
          transition: width 0.3s cubic-bezier(0.4, 0, 0.2, 1),
                      transform 0.3s ease;
          display: flex;
          flex-direction: column;
          z-index: 50;
          overflow: hidden;
        }

        /* ===== MOBILE SLIDE ANIMATION ===== */
        @media (max-width: 768px) {
          .rs-sidebar {
            animation: slideInLeft 0.3s ease;
            max-width: 320px;
          }
        }

        /* ===== CLOSE BUTTON (Mobile) ===== */
        .rs-close-btn {
          position: absolute;
          top: 14px;
          right: 14px;
          padding: 8px;
          background: rgba(255, 0, 85, 0.08);
          border: none;
          cursor: pointer;
          border-radius: 10px;
          color: #ff0055;
          transition: all 0.2s ease;
          z-index: 10;
        }

        .rs-close-btn:hover {
          background: rgba(255, 0, 85, 0.15);
          transform: rotate(90deg);
        }

        /* ===== LOGO SECTION ===== */
        .rs-logo-container {
          padding: 18px 16px;
          border-bottom: 1px solid #f0f0f0;
          display: flex;
          align-items: center;
          gap: 12px;
          flex-shrink: 0;
          background: linear-gradient(135deg, #fffafc, #fff);
        }

        .rs-logo-image {
          border-radius: 12px;
          object-fit: cover;
          flex-shrink: 0;
          border: 2px solid rgba(255, 0, 85, 0.15);
          box-shadow: 0 4px 12px rgba(255, 0, 85, 0.1);
        }

        .rs-logo-title {
          font-size: 18px;
          font-weight: 800;
          color: #1a0a10;
          margin: 0;
          line-height: 1.2;
          white-space: nowrap;
        }

        .rs-logo-highlight {
          background: linear-gradient(135deg, #ff0055, #ff4d88);
          -webkit-background-clip: text;
          background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .rs-logo-subtitle {
          font-size: 10.5px;
          color: #999;
          margin: 2px 0 0;
          font-weight: 500;
        }

        /* ===== NAVIGATION ===== */
        .rs-nav {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 12px 0;
          scrollbar-width: thin;
          scrollbar-color: #ffc2d1 transparent;
        }

        .rs-nav::-webkit-scrollbar {
          width: 4px;
        }

        .rs-nav::-webkit-scrollbar-thumb {
          background: #ffc2d1;
          border-radius: 999px;
        }

        .rs-nav::-webkit-scrollbar-track {
          background: transparent;
        }

        .rs-nav-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .rs-nav-item {
          margin-bottom: 3px;
          padding: 0 8px;
        }

        .rs-nav-link {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 11px 14px;
          border-radius: 10px;
          text-decoration: none;
          color: #4b5563;
          transition: all 0.25s ease;
          font-size: 14px;
          font-weight: 500;
          position: relative;
        }

        .rs-nav-link:hover {
          background: linear-gradient(135deg, #fff5f7, #fff0f3);
          color: #ff0055;
          transform: translateX(2px);
        }

        .rs-nav-link.active {
          background: linear-gradient(135deg, #ff0055, #ff4d88);
          color: #fff;
          box-shadow: 0 4px 14px rgba(255, 0, 85, 0.3);
          font-weight: 700;
        }

        .rs-nav-link.active:hover {
          transform: translateX(0);
        }

        .rs-nav-icon {
          font-size: 18px;
          flex-shrink: 0;
          transition: transform 0.25s ease;
        }

        .rs-nav-link:hover .rs-nav-icon {
          transform: scale(1.1);
        }

        .rs-nav-label {
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        /* ===== COLLAPSED STATE (Desktop 80px) ===== */
        .rs-sidebar.collapsed .rs-nav-link {
          justify-content: center;
          padding: 12px 8px;
        }

        .rs-sidebar.collapsed .rs-nav-label {
          display: none;
        }

        .rs-sidebar.collapsed .rs-nav-icon {
          font-size: 20px;
        }

        /* ===== TOOLTIP for collapsed state ===== */
        .rs-sidebar.collapsed .rs-nav-link {
          position: relative;
        }

        .rs-sidebar.collapsed .rs-nav-link:hover::after {
          content: attr(data-label);
          position: absolute;
          left: 100%;
          top: 50%;
          transform: translateY(-50%);
          margin-left: 12px;
          background: #1a0a10;
          color: #fff;
          padding: 6px 12px;
          border-radius: 8px;
          font-size: 12px;
          white-space: nowrap;
          z-index: 100;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
          pointer-events: none;
        }

        /* ===== LOGOUT SECTION ===== */
        .rs-logout-container {
          padding: 14px;
          border-top: 1px solid #f0f0f0;
          flex-shrink: 0;
          background: linear-gradient(135deg, #fffafc, #fff);
        }

        .rs-logout-btn {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 11px 14px;
          background: transparent;
          border: 1.5px solid rgba(239, 68, 68, 0.15);
          border-radius: 10px;
          color: #ef4444;
          cursor: pointer;
          width: 100%;
          transition: all 0.25s ease;
          font-size: 14px;
          font-weight: 600;
        }

        .rs-logout-btn:hover {
          background: linear-gradient(135deg, #fef2f2, #fee2e2);
          border-color: rgba(239, 68, 68, 0.3);
          transform: translateY(-1px);
        }

        .rs-logout-icon {
          font-size: 18px;
          flex-shrink: 0;
        }

        .rs-sidebar.collapsed .rs-logout-btn {
          justify-content: center;
          padding: 12px 8px;
        }

        .rs-sidebar.collapsed .rs-logout-label {
          display: none;
        }

        /* ===== TABLET (max 1024px) ===== */
        @media (max-width: 1024px) {
          .rs-nav-link {
            padding: 10px 12px;
            font-size: 13.5px;
          }

          .rs-nav-icon {
            font-size: 17px;
          }
        }

        /* ===== MOBILE (max 768px) ===== */
        @media (max-width: 768px) {
          .rs-logo-container {
            padding: 16px 14px;
            padding-right: 50px;
          }

          .rs-logo-image {
            width: 40px !important;
            height: 40px !important;
          }

          .rs-logo-title {
            font-size: 17px;
          }

          .rs-logo-subtitle {
            font-size: 10px;
          }

          .rs-nav {
            padding: 10px 0;
          }

          .rs-nav-item {
            padding: 0 10px;
          }

          .rs-nav-link {
            padding: 12px 14px;
            font-size: 14px;
          }

          .rs-nav-icon {
            font-size: 18px;
          }

          .rs-logout-container {
            padding: 14px 12px;
          }

          .rs-logout-btn {
            padding: 12px 14px;
          }
        }

        /* ===== SMALL MOBILE (max 480px) ===== */
        @media (max-width: 480px) {
          .rs-logo-container {
            padding: 14px 12px;
            padding-right: 48px;
            gap: 10px;
          }

          .rs-logo-image {
            width: 36px !important;
            height: 36px !important;
          }

          .rs-logo-title {
            font-size: 15.5px;
          }

          .rs-logo-subtitle {
            font-size: 9.5px;
          }

          .rs-close-btn {
            top: 12px;
            right: 12px;
            padding: 7px;
          }

          .rs-nav-item {
            padding: 0 8px;
          }

          .rs-nav-link {
            padding: 11px 12px;
            font-size: 13.5px;
            gap: 11px;
          }

          .rs-nav-icon {
            font-size: 17px;
          }

          .rs-logout-container {
            padding: 12px 10px;
          }

          .rs-logout-btn {
            padding: 11px 12px;
            font-size: 13.5px;
          }
        }

        /* ===== TINY MOBILE (max 360px) ===== */
        @media (max-width: 360px) {
          .rs-logo-container {
            padding: 12px 10px;
            padding-right: 44px;
            gap: 8px;
          }

          .rs-logo-image {
            width: 34px !important;
            height: 34px !important;
          }

          .rs-logo-title {
            font-size: 14px;
          }

          .rs-logo-subtitle {
            font-size: 9px;
          }

          .rs-nav-link {
            padding: 10px;
            font-size: 13px;
            gap: 10px;
          }

          .rs-nav-icon {
            font-size: 16px;
          }
        }

        /* ===== HOVER EFFECTS ===== */
        .modal-btn-cancel:hover {
          background: #e8e8e8 !important;
          transform: translateY(-2px);
        }

        .modal-btn-logout:hover {
          box-shadow: 0 10px 28px rgba(244,67,54,0.4) !important;
          transform: translateY(-2px);
        }

        .modal-close-btn:hover {
          color: #f44336 !important;
          transform: rotate(90deg);
        }

        /* ===== SAFE AREA (iOS) ===== */
        @supports (padding: max(0px)) {
          .rs-sidebar {
            padding-top: max(0px, env(safe-area-inset-top));
          }
        }

        /* ===== LANDSCAPE MOBILE ===== */
        @media (max-height: 500px) and (orientation: landscape) {
          .rs-logo-container {
            padding: 10px 14px;
          }

          .rs-logo-image {
            width: 32px !important;
            height: 32px !important;
          }

          .rs-nav-link {
            padding: 8px 12px;
          }

          .rs-logout-container {
            padding: 8px 12px;
          }

          .rs-logout-btn {
            padding: 8px 12px;
          }
        }

        /* ===== ACCESSIBILITY ===== */
        .rs-nav-link:focus-visible,
        .rs-logout-btn:focus-visible {
          outline: 2px solid #ff0055;
          outline-offset: 2px;
        }

        /* Tap highlight remove */
        .rs-nav-link,
        .rs-logout-btn,
        .rs-close-btn {
          -webkit-tap-highlight-color: transparent;
        }
      `}</style>

      <aside
        className={`rs-sidebar ${!isOpen && !isMobile ? 'collapsed' : ''}`}
        style={{
          width: getSidebarWidth(),
          transform: isMobile && !isOpen ? 'translateX(-100%)' : 'translateX(0)',
        }}
      >
        {/* Close Button - Mobile */}
        {isMobile && isOpen && (
          <button
            className="rs-close-btn"
            onClick={() => setIsOpen(false)}
            aria-label="Close menu"
          >
            <FaTimes size={18} />
          </button>
        )}

        {/* Logo */}
        <Link 
          to="/" 
          className="rs-logo-container" 
          onClick={closeSidebar} // Mobile menu mein click karne par sidebar close ho jaye
          style={{ textDecoration: 'none', display: 'flex' }}
        >
          <img
            src={logoImg}
            alt="Community Rishtey Logo"
            className="rs-logo-image"
            style={{
              width: isOpen ? '42px' : '36px',
              height: isOpen ? '42px' : '36px',
            }}
          />
          {(isOpen || isMobile) && (
            <div>
              <h1 className="rs-logo-title">
                Community <span className="rs-logo-highlight">Rishtey</span>
              </h1>
              <p className="rs-logo-subtitle">Find Your Perfect Match</p>
            </div>
          )}
        </Link>

        {/* Navigation */}
        <nav className="rs-nav">
          <ul className="rs-nav-list">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.path} className="rs-nav-item">
                  <NavLink
                    to={item.path}
                    onClick={closeSidebar}
                    data-label={item.label}
                    className={({ isActive }) =>
                      `rs-nav-link ${isActive ? 'active' : ''}`
                    }
                  >
                    <Icon className="rs-nav-icon" />
                    <span className="rs-nav-label">{item.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Logout */}
        <div className="rs-logout-container">
          <button
            onClick={handleLogoutClick}
            className="rs-logout-btn"
            aria-label="Logout"
          >
            <FaSignOutAlt className="rs-logout-icon" />
            <span className="rs-logout-label">Logout</span>
          </button>
        </div>
      </aside>

      {/* ===== LOGOUT MODAL ===== */}
      {showLogoutModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(6px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'fadeIn 0.3s ease',
            padding: '16px',
          }}
          onClick={handleLogoutCancel}
        >
          <div
            style={{
              background: '#fff',
              borderRadius: '20px',
              maxWidth: '420px',
              width: '100%',
              padding: isSmallMobile ? '24px 20px 20px' : '32px 28px 28px',
              boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
              textAlign: 'center',
              animation: 'slideUp 0.35s ease',
              position: 'relative',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close-btn"
              style={{
                position: 'absolute',
                top: '14px',
                right: '14px',
                background: 'none',
                border: 'none',
                fontSize: '20px',
                color: '#bbb',
                cursor: 'pointer',
                transition: 'all .3s ease',
                padding: '4px',
                lineHeight: 1,
              }}
              onClick={handleLogoutCancel}
              aria-label="Close"
            >
              <FaTimesCircle />
            </button>

            <div
              style={{
                width: isSmallMobile ? '60px' : '72px',
                height: isSmallMobile ? '60px' : '72px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #fff0f3, #ffe4ec)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                fontSize: isSmallMobile ? '28px' : '34px',
                color: '#f44336',
              }}
            >
              <FaExclamationTriangle />
            </div>

            <h3
              style={{
                fontSize: isSmallMobile ? '19px' : '22px',
                fontWeight: 800,
                color: '#1a0a10',
                margin: '0 0 8px',
              }}
            >
              Logout Confirmation
            </h3>

            <p
              style={{
                fontSize: isSmallMobile ? '13px' : '14px',
                color: '#888',
                margin: '0 0 20px',
                lineHeight: 1.6,
              }}
            >
              Are you sure you want to logout? <br />
              You will need to login again to access your account.
            </p>

            <p
              style={{
                fontSize: '13px',
                color: '#ff0055',
                fontWeight: 600,
                margin: '0 0 20px',
              }}
            >
              👤{' '}
              <span style={{ color: '#ff0055', fontWeight: 700 }}>
                {getUsername()}
              </span>
            </p>

            <div
              style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'center',
                flexDirection: isSmallMobile ? 'column' : 'row',
              }}
            >
              <button
                className="modal-btn-cancel"
                style={{
                  padding: isSmallMobile ? '11px 20px' : '12px 28px',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: isSmallMobile ? '13px' : '14px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all .3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  justifyContent: 'center',
                  minWidth: isSmallMobile ? 'auto' : '120px',
                  background: '#f5f5f5',
                  color: '#666',
                  width: isSmallMobile ? '100%' : 'auto',
                }}
                onClick={handleLogoutCancel}
              >
                <FaTimes size={13} /> No, Stay
              </button>
              <button
                className="modal-btn-logout"
                style={{
                  padding: isSmallMobile ? '11px 20px' : '12px 28px',
                  border: 'none',
                  borderRadius: '10px',
                  fontSize: isSmallMobile ? '13px' : '14px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all .3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  justifyContent: 'center',
                  minWidth: isSmallMobile ? 'auto' : '120px',
                  background: 'linear-gradient(135deg, #f44336, #d32f2f)',
                  color: '#fff',
                  boxShadow: '0 6px 18px rgba(244,67,54,0.3)',
                  width: isSmallMobile ? '100%' : 'auto',
                }}
                onClick={handleLogoutConfirm}
              >
                <FaCheckCircle size={13} /> Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Sidebar;